import { Brand, Module } from "@apis/index";
import AccessDenied from "@components/access-denied";
import { deletesvg, edit, eye, switchOff, switchOn } from "@assets/icons";
import DeleteModal from "@components/delete-modal";
import Svg from "@components/svg";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import useConfig from "@hooks/useConfig";
import { RootState } from "@store/index";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryState } from "@hooks/useQueryState";

const BrandTable = ({ moduleId }) => {
  const { bucketUrl } = useConfig();
  const {
    items: brands = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.brands);
  const { showModal } = useModal();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");

  const [error, setError] = useState<string | null>(null);

  const fetchBrands = async () => {
    setError(null);
    try {
      await Brand.list({
        state: "brands",
        params: { page, moduleId, search },
      });
    } catch (err: any) {
      console.error("Failed to fetch brands:", err);
      setError(err.message || "Failed to load brands");
    }
  };

  useEffect(() => {
    if (moduleId) fetchBrands();
  }, [moduleId, page, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const fallbackImage = "https://via.placeholder.com/80x40";
  const fallbackBanner = "https://via.placeholder.com/80x40";
  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => item?.id,
    },
    {
      title: "Brand Name",
      key: "name",
      render: (item: any) => item?.name,
    },
    {
      title: "Module",
      key: "module",
      render: (item: any) => item.module?.name || "-",
    },
    {
      title: "Icon Image",
      key: "image",
      render: (item: any) => (
        <img
          src={item.image ? `${bucketUrl}/${item.image}` : fallbackImage}
          alt={item.name}
          style={{
            width: 60,
            height: 60,
            borderRadius: "6px",
            objectFit: "cover",
          }}
        />
      ),
    },
    {
      title: "Banner",
      key: "banner",
      render: (item: any) => (
        <div
          style={{ display: "flex", justifyContent: "center" }}
          onClick={() => console.log(item)}
        >
          <div
            style={{
              display: "flex",
              overflow: "scroll",
              maxWidth: "170px",
              height: "70px",
              gap: "12px",
            }}
          >
            {item?.banners?.map((b) => (
              <img
                src={b.image ? `${bucketUrl}/${b.image}` : fallbackBanner}
                alt={item.name}
                style={{ width: "100%", height: "100%", borderRadius: "4px" }}
              />
            ))}
          </div>
        </div>
      ),
    },
  ];
  const navigate = useNavigate();

  if (error) {
    return (
      <AccessDenied
        message={error}
        onRetry={fetchBrands}
      />
    );
  }

  return (
    <Table
      data={brands}
      columns={columns}
      totalItems={totalItems}
      totalPages={totalPages}
      pageControlled={page}
      onPageChange={setPage}
      itemsPerPage={30}
      hasSearchbar={true}
      onSearchChange={setSearch}
      actions={[
        { key: "view", icon: eye, onClick: () => {} },
        {
          key: "edit",
          icon: edit,
          onClick: (item) => {
            navigate(
              `/admin/product-management/brands/${item.id}?module=${item.module.name}`,
              {
                state: item,
              }
            );
          },
        },
        {
          key: "remove",
          icon: deletesvg,
          onClick: async (item) => {
            showModal(
              <DeleteModal
                state="brands"
                api={Brand}
                id={item.id}
                onSuccess={() => fetchBrands()}
              />,
            );
          },
        },
      ]}
    />
  );
};

export default BrandTable;
